import React, { useRef, useEffect } from 'react';
import StyledTable from './StyledTable';
import { Spin, Typography } from 'antd';
import InfiniteScroll from 'react-infinite-scroll-component';
import { greyScaleTokens } from '../../../theme/theme';

/**
 * ProductVariantsTable - Componente riutilizzabile per visualizzare prodotti e varianti
 * 
 * @param {Object} props - Proprietà del componente
 * @param {Array} props.columns - Array di definizioni delle colonne per la tabella dei prodotti
 * @param {Array} props.products - Array di prodotti da mostrare nella tabella
 * @param {boolean} props.loading - Stato di caricamento della tabella
 * @param {number} props.currentPage - Pagina corrente
 * @param {number} props.pageSize - Dimensione della pagina
 * @param {number} props.totalProducts - Numero totale di prodotti
 * @param {Array} props.expandedRowKeys - Array di chiavi delle righe espanse
 * @param {Function} props.handleTableChange - Handler per cambiamenti nella tabella
 * @param {Array} props.variantColumns - Array di definizioni delle colonne per la tabella delle varianti
 * @param {Object} props.variants - Oggetto con le varianti per ogni prodotto
 * @param {Function} props.setExpandedRowKeys - Funzione per impostare le chiavi delle righe espanse (deprecated)
 * @param {Function} props.onRowExpand - Funzione per gestire l'espansione con lazy loading
 * @param {boolean} props.isFiltersApplied - Flag che indica se i filtri sono applicati
 * @param {string} props.searchTerm - Termine di ricerca
 * @param {Function} props.reorderColumnsFunction - Funzione opzionale per riordinare le colonne
 * @param {Function} props.renderExpandedRowActions - Funzione opzionale per renderizzare azioni aggiuntive nella riga espansa
 * @returns {JSX.Element}
 */
const ProductVariantsTable = ({
    columns,
    products,
    loading,
    loadingMore, // Nuova prop
    loadingVariants, // Nuova prop per il loading delle varianti
    hasMore,     // Nuova prop
    handleLoadMore, // Nuova prop
    expandedRowKeys,
    variantColumns,
    variants,
    setExpandedRowKeys, // Manteniamo per backward compatibility
    onRowExpand, // Nuova prop per lazy loading
    reorderColumnsFunction,
    renderExpandedRowActions
}) => {
    // Rimuovo la logica dell'IntersectionObserver
    // const observerTarget = useRef(null);
    // useEffect(...);

    // Funzione predefinita per riordinare le colonne se non viene fornita
    const defaultReorderColumns = (originalColumns) => {
        const columnOrder = ['imgUrl', 'modelName', 'variantsCount', 'priority', 'brandName', 'lastUpdate', 'select', 'visibility', 'delete'];
        const columnMap = {};

        // Crea una mappa delle colonne esistenti preservando le proprietà originali
        originalColumns.forEach(col => {
            // Preserva tutte le proprietà originali delle colonne
            const colWithWidth = { ...col };

            // Aggiungi solo proprietà specifiche per colonne speciali
            switch (col.key) {
                case 'select':
                    colWithWidth.fixed = 'right';
                    colWithWidth.align = 'center';
                    break;
                case 'visibility':
                    colWithWidth.fixed = 'right';
                    colWithWidth.align = 'center';
                    break;
                case 'delete':
                    colWithWidth.fixed = 'right';
                    colWithWidth.align = 'center';
                    break;
                default:
                    // Per tutte le altre colonne, mantieni le proprietà originali
                    break;
            }

            columnMap[col.key] = colWithWidth;
        });

        // Riorganizza le colonne nell'ordine desiderato
        return columnOrder
            .filter(key => columnMap[key]) // Filtra solo le chiavi presenti
            .map(key => columnMap[key]);
    };

    // Usa la funzione di riordino fornita o quella predefinita
    const modifiedColumns = reorderColumnsFunction
        ? reorderColumnsFunction(columns)
        : defaultReorderColumns(columns);

    // Funzione per renderizzare le righe espanse con le varianti
    const expandedRowRender = (record) => (
        <div style={{ padding: "clamp(0.5rem, 0.4rem + 0.5vw, 0.625rem)" }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'clamp(0.5rem, 0.4rem + 0.5vw, 0.75rem)',
            }}>
                <span style={{
                    fontSize: "clamp(0.875rem, 0.8rem + 0.25vw, 1rem)",
                    fontWeight: 'bold'
                }}>
                    Available variants:
                </span>
                {/* Renderizza le azioni aggiuntive se la funzione è fornita */}
                {renderExpandedRowActions && renderExpandedRowActions(record)}
            </div>
            <StyledTable
                columns={variantColumns}
                dataSource={(variants[record.id] || [])
                    .map(variant => ({
                        ...variant,
                        productId: record.id
                    }))}
                rowKey="id"
                loading={loadingVariants && (!variants[record.id] || variants[record.id].length === 0)}
                pagination={null}
                size="middle"
                scroll={{ x: 980 }}
                containerStyle={{
                    boxShadow: 'none',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    marginTop: '10px'
                }}
            />
        </div>
    );

    // Funzione per gestire l'espansione delle righe
    const onExpand = (expanded, record) => {
        // Se è fornita onRowExpand (lazy loading), usa quella
        if (onRowExpand) {
            onRowExpand(expanded, record);
        } else if (setExpandedRowKeys) {
            // Fallback per backward compatibility
            if (expanded) {
                setExpandedRowKeys(prev => [...prev, record.id]);
            } else {
                setExpandedRowKeys(prev => prev.filter(key => key !== record.id));
            }
        }
    };

    return (
        <InfiniteScroll
            dataLength={products.length}
            next={handleLoadMore}
            hasMore={hasMore}
            loader={
                <div style={{ textAlign: 'center', padding: '1rem' }}>
                    <Spin />
                </div>
            }
            endMessage={
                products.length > 0 ? (
                    <p style={{ textAlign: 'center', color: greyScaleTokens.grey600, fontStyle: 'italic' }}>
                        All products have been loaded
                    </p>
                ) : null
            }
            scrollableTarget="page-content-wrapper"
        >
            <StyledTable
                columns={modifiedColumns}
                dataSource={products}
                rowKey="id"
                loading={loading && products.length === 0}
                pagination={null} // Disabilita paginazione
                size="middle"
                scroll={{ x: 1000 }}
                containerStyle={{ boxShadow: 'none' }}
                expandable={{
                    expandedRowRender,
                    expandedRowKeys,
                    onExpand
                }}
            />
        </InfiniteScroll>
    );
};

export default ProductVariantsTable;
